import { initializeWasm, Repo } from "@automerge/vanillajs/slim";
import { IndexedDBStorageAdapter } from "@automerge/automerge-repo-storage-indexeddb";
import { loadOrCreateSigner } from "@inkandswitch/patchwork-bootloader/signer";
// eslint-disable-next-line
// @ts-ignore — the default init is a wasm-bindgen runtime helper not in the .d.ts
import initSubduction from "@automerge/automerge-subduction/slim";
import { keyhiveStorageName, storagePrefix, } from "@inkandswitch/patchwork-bootloader/storage";
import debug from "debug";
const log = debug("patchwork:setup:repo");
const syncServer = typeof __SYNC_SERVER__ !== "undefined"
    ? __SYNC_SERVER__
    : { url: "wss://subduction.sync.inkandswitch.com" };
// Fetch and initialize automerge + subduction wasm. Memoized: the fetches start
// on the first call and every later caller awaits the same init. Skipped
// entirely when the site brings its own Repo (it did this itself).
let wasmReady;
export function initWasm() {
    if (!wasmReady) {
        wasmReady = Promise.all([
            initializeWasm(new Request("/automerge.wasm")),
            initSubduction(new Request("/subduction.wasm")),
        ]).then(() => undefined);
    }
    return wasmReady;
}
/**
 * The tab's own node: this origin's IndexedDB, opened on this thread, a socket
 * to the sync server, and the storage channel to every other Repo on the
 * origin. Nothing is shared with other tabs except the database underneath
 * and the identity every node on the origin signs with.
 */
export async function createRepo() {
    if (syncServer.keyhive) {
        log("setting up keyhive");
        const { initKeyhiveWasm, initializeAutomergeRepoKeyhive } = await import("@automerge/automerge-repo-keyhive");
        initKeyhiveWasm();
        const { hive, repo } = await initializeAutomergeRepoKeyhive({
            // ARK injects an `idFactory` deriving document ids from keyhive. A site
            // can opt out of it with `keyhive: { useIdFactory: false }`.
            createRepo: ({ idFactory, ...repoConfig }) => new Repo(syncServer.useIdFactory === false
                ? repoConfig
                : { ...repoConfig, idFactory }),
            storage: new IndexedDBStorageAdapter(keyhiveStorageName),
            peerIdSuffix: storagePrefix + Math.random().toString(36).slice(2),
            automaticArchiveIngestion: true,
            cachingMode: "periodic",
            // ARK selects the relay via `syncServer`, defaulting to "subduction".
            syncServer: syncServer.keyhive,
            repo: {
                storage: new IndexedDBStorageAdapter(),
                subductionWebsocketEndpoints: [syncServer.url],
                headsChannel: `${storagePrefix}-heads`,
                enableRemoteHeadsGossiping: true,
            },
        });
        log("keyhive setup complete");
        return { repo, hive };
    }
    // The signer is explicit rather than the Repo's internal default so that
    // every tab on this origin signs as the same peer, and so the identity the
    // tab presents to the server can be shown on window.patchwork.
    const storage = new IndexedDBStorageAdapter();
    const signer = await loadOrCreateSigner(storage);
    const repo = new Repo({
        signer,
        storage,
        peerId: `${storagePrefix}-tab-${crypto.randomUUID()}`,
        subductionWebsocketEndpoints: [syncServer.url],
        headsChannel: `${storagePrefix}-heads`,
        enableRemoteHeadsGossiping: true,
    });
    const signerIdentity = {
        peerId: signer.peerId().toString(),
        verifyingKey: signer.verifyingKey().toHex(),
    };
    log("repo created, tab subduction identity:", signerIdentity);
    return { repo, signerIdentity };
}
